// AI CONTEXT: seo - Reusable helpers for meta tags and JSON-LD
// Purpose: Set per-page title, description, Open Graph, Twitter Card, and canonical URL
// Usage: import { setSEO, injectJsonLd } from '../utils/seo';

export function setOrCreateMeta(selector, createAttrs, valueAttr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(valueAttr, value);
  return el;
}

export function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
  return link;
}

export function absoluteUrl(pathOrUrl, siteBase = 'https://tolberttactical.com') {
  if (!pathOrUrl) return siteBase;
  try {
    // if already absolute
    // eslint-disable-next-line no-new
    new URL(pathOrUrl);
    return pathOrUrl;
  } catch (_) {
    return `${siteBase}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
  }
}

export function setSEO({ title, description, path = '/', image, siteName = 'Tolbert Tactical' }) {
  try {
    const siteBase = 'https://tolberttactical.com';
    const url = absoluteUrl(path, siteBase);
    const ogImage = absoluteUrl(image || '/files/Logo.png', siteBase);

    if (title) document.title = title;
    if (description) setOrCreateMeta('meta[name="description"]', { name: 'description' }, 'content', description);

    // Open Graph
    setOrCreateMeta('meta[property="og:title"]', { property: 'og:title' }, 'content', title || siteName);
    setOrCreateMeta('meta[property="og:description"]', { property: 'og:description' }, 'content', description || '');
    setOrCreateMeta('meta[property="og:type"]', { property: 'og:type' }, 'content', 'website');
    setOrCreateMeta('meta[property="og:url"]', { property: 'og:url' }, 'content', url);
    setOrCreateMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'content', siteName);
    setOrCreateMeta('meta[property="og:image"]', { property: 'og:image' }, 'content', ogImage);

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'content', 'summary_large_image');
    setOrCreateMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', title || siteName);
    setOrCreateMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', description || '');
    setOrCreateMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', ogImage);

    // Canonical
    setCanonical(url);
  } catch (e) {
    // no-op in SSR or restricted environments
  }
}

export function injectJsonLd(id, data) {
  try {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  } catch (e) {
    // ignore
  }
}
